import {
  IonImageryProvider,
  ArcGisMapServerImageryProvider,
  OpenStreetMapImageryProvider,
  UrlTemplateImageryProvider,
  WebMercatorTilingScheme,
  Rectangle,
  Color,
  TileMapServiceImageryProvider,
} from "cesium";
import { saveToCache } from "./Utility";

const addArcGISWorldImagery = async (viewer) => {
  const provider = await ArcGisMapServerImageryProvider.fromUrl("https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer");

  viewer.imageryLayers.removeAll();
  viewer.imageryLayers.addImageryProvider(provider);
};

const loadNaturalEarthFromIon = async (viewer) => {
  const imageryProvider = await IonImageryProvider.fromAssetId(3813);

  viewer.imageryLayers.removeAll();
  viewer.imageryLayers.addImageryProvider(imageryProvider);
};

const loadBingMapRoadFromIon = async (viewer) => {
  const imageryProvider = await IonImageryProvider.fromAssetId(4);

  viewer.imageryLayers.removeAll();
  viewer.imageryLayers.addImageryProvider(imageryProvider);
};

const loadBingMapsAerialwithLabelsFromIon = async (viewer) => {
  const imageryProvider = await IonImageryProvider.fromAssetId(3);

  viewer.imageryLayers.removeAll();
  viewer.imageryLayers.addImageryProvider(imageryProvider);
};

const loadBingMapsAerialFromIon = async (viewer) => {
  const imageryProvider = await IonImageryProvider.fromAssetId(2);

  viewer.imageryLayers.removeAll();
  viewer.imageryLayers.addImageryProvider(imageryProvider);
};

const loadOpenStreetMap = async (viewer) => {
  viewer.imageryLayers.removeAll();
  viewer.imageryLayers.addImageryProvider(new OpenStreetMapImageryProvider());
};

const loadEarthAtNightFromIon = async (viewer) => {
  const imageryProvider = await IonImageryProvider.fromAssetId(3812);

  viewer.imageryLayers.removeAll();
  viewer.imageryLayers.addImageryProvider(imageryProvider);
};

const loadMapboxImagery = async (viewer, mapboxStyle = "streets-v11") => {
  const accessToken = process.env.REACT_APP_MAPBOX_KEY;
  const templateUrl = `https://api.mapbox.com/styles/v1/mapbox/${mapboxStyle}/tiles/256/{z}/{x}/{y}@2x?access_token=${accessToken}`;

  const imageryProvider = new UrlTemplateImageryProvider({
    url: templateUrl,
    tilingScheme: new WebMercatorTilingScheme(),
    maximumLevel: 18,
    credit: "© Mapbox",
  });

  viewer.imageryLayers.removeAll();
  viewer.imageryLayers.addImageryProvider(imageryProvider);
};

const loadGray = async (viewer) => {
  viewer.imageryLayers.removeAll();
  viewer.scene.globe.baseColor = Color.fromCssColorString("rgba(100, 100, 100, 0.8)");
  viewer.scene.globe.imageryLayers.removeAll();
};

const loadWhite = async (viewer) => {
  viewer.imageryLayers.removeAll();
  viewer.scene.globe.baseColor = Color.fromCssColorString("rgb(255, 255, 255)");
  viewer.scene.globe.imageryLayers.removeAll();
};

const loadColor = async (viewer, colorCode) => {
  viewer.imageryLayers.removeAll();

  viewer.scene.globe.baseColor = Color.fromCssColorString(colorCode);
  viewer.scene.globe.imageryLayers.removeAll();
};

// Handles provider init + tile errors (CORS/404/etc) without crashing the app.
export const loadGeoTiffTileFromIon = async (viewer, idOrUrl, options = {}) => {
  const { maxTileErrors = 5, replaceExisting, onError = () => {} } = options;

  const logOnly = (e) => {
    try {
      console.error(e);
    } catch {}
    try {
      onError(e);
    } catch {}
  };

  if (!viewer || !viewer.imageryLayers) {
    logOnly(new Error("Invalid Cesium viewer"));
    return null;
  }

  const layers = viewer.imageryLayers;

  const isNumeric = typeof idOrUrl === "number" || /^[0-9]+$/.test(String(idOrUrl));

  const shouldReplaceExisting = typeof replaceExisting === "boolean" ? replaceExisting : isNumeric;
  const isOverlayMode = !shouldReplaceExisting;

  const previousLayers = [];
  for (let i = layers.length - 1; i >= 0; i--) previousLayers.push(layers.get(i));

  let previousRemoved = false;

  const isValidRectangle = (r) => {
    if (!r || typeof r !== "object") return false;
    const w = r.west,
      s = r.south,
      e = r.east,
      n = r.north;
    return [w, s, e, n].every((v) => typeof v === "number" && Number.isFinite(v));
  };

  const ensureProviderRectangleOrFail = (provider) => {
    const rect = provider?.rectangle ?? provider?.tilingScheme?.rectangle;
    if (!isValidRectangle(rect)) {
      const err = new Error("ImageryProvider rectangle is undefined/invalid. Layer will NOT be added to prevent Cesium render crash.");
      logOnly(err);
      return false;
    }
    if (!provider.rectangle) {
      try {
        provider.rectangle = rect;
      } catch {
        // ignore
      }
    }
    return true;
  };

  let provider;
  try {
    if (isNumeric) {
      const assetId = typeof idOrUrl === "number" ? idOrUrl : parseInt(String(idOrUrl), 10);
      provider = await IonImageryProvider.fromAssetId(assetId);
    } else {
      const normalizeTmsUrl = (rawUrl) => {
        if (rawUrl == null) return rawUrl;
        let u = String(rawUrl).trim();
        u = u.replace(/\/tilemapresource\.xml$/i, "/");
        if (!u.endsWith("/")) u += "/";
        return u;
      };

      const baseUrl = normalizeTmsUrl(idOrUrl);

      const tryLoadFromTileJson = async () => {
        const tileJsonUrl = `${baseUrl}tiles.json`;
        const res = await fetch(tileJsonUrl, { method: "GET" });
        if (!res.ok) return null;

        const tj = await res.json();
        const tiles = Array.isArray(tj?.tiles) ? tj.tiles : [];
        const template = tiles[0];
        const bounds = Array.isArray(tj?.bounds) ? tj.bounds : null;
        if (!template || !bounds || bounds.length !== 4) return null;

        let urlTemplate = `${baseUrl}${String(template).replace(/^\/+/, "")}`;

        const scheme = String(tj?.scheme || "").toLowerCase();
        if (scheme === "tms") {
          urlTemplate = urlTemplate.replace(/{y}/g, "{reverseY}");
        }

        const w = Number(bounds[0]);
        const s = Number(bounds[1]);
        const e = Number(bounds[2]);
        const n = Number(bounds[3]);
        if (![w, s, e, n].every(Number.isFinite)) return null;

        const rectangle = Rectangle.fromDegrees(w, s, e, n);
        const minzoom = Number.isFinite(tj?.minzoom) ? tj.minzoom : undefined;
        const maxzoom = Number.isFinite(tj?.maxzoom) ? tj.maxzoom : undefined;

        const p = new UrlTemplateImageryProvider({
          url: urlTemplate,
          tilingScheme: new WebMercatorTilingScheme(),
          rectangle,
          minimumLevel: minzoom,
          maximumLevel: maxzoom,
        });
        p.enablePickFeatures = false;
        return p;
      };

      try {
        provider = await tryLoadFromTileJson();
      } catch {
        // ignore and fall back
      }

      if (!provider) {
        provider = await TileMapServiceImageryProvider.fromUrl(baseUrl);
      }
    }

    if (provider && provider.readyPromise) {
      await provider.readyPromise.catch((e) => {
        throw e;
      });
    }

    // Critical: prevent Cesium render crash if rectangle is missing/invalid.
    if (!ensureProviderRectangleOrFail(provider)) {
      return null;
    }
  } catch (e) {
    logOnly(e);
    return null;
  }

  let newLayer;
  try {
    if (isOverlayMode) {
      for (let i = layers.length - 1; i >= 0; i--) {
        const layer = layers.get(i);
        if (layer && layer.__atlasGeoTiffTileOverlay === true) {
          try {
            layers.remove(layer, true);
          } catch {}
        }
      }
    }

    newLayer = layers.addImageryProvider(provider);
    newLayer.__atlasGeoTiffTileOverlay = true;
  } catch (e) {
    logOnly(e);
    return null;
  }

  let tileErrorCount = 0;
  let disposed = false;

  const dispose = () => {
    if (disposed) return;
    disposed = true;
    try {
      if (provider?.errorEvent) provider.errorEvent.removeEventListener(onProviderError);
    } catch {}
  };

  const restorePrevious = () => {
    try {
      layers.remove(newLayer, true);
    } catch {}
    if (previousRemoved) {
      try {
        for (let i = previousLayers.length - 1; i >= 0; i--) {
          const layer = previousLayers[i];
          try {
            layers.add(layer);
          } catch {}
        }
      } catch {}
      previousRemoved = false;
    }
  };

  const onProviderError = (err) => {
    if (err) err.retry = false;

    const statusCode = err?.statusCode;
    const message = String(err?.message || "");
    const isMissingTile = statusCode === 403 || statusCode === 404 || /\b(403|404)\b/.test(message);
    if (isMissingTile) {
      logOnly(err);
      return;
    }

    if (isOverlayMode) {
      logOnly(err);
      return;
    }

    tileErrorCount += 1;
    logOnly(err);

    if (tileErrorCount >= maxTileErrors) {
      restorePrevious();
      dispose();
    }
  };

  if (provider?.errorEvent) provider.errorEvent.addEventListener(onProviderError);

  if (shouldReplaceExisting) {
    setTimeout(() => {
      if (disposed) return;
      if (tileErrorCount === 0) {
        try {
          for (const l of previousLayers) layers.remove(l, false);
          previousRemoved = true;
        } catch {}
      }
    }, 2000);
  }

  return newLayer;
};

export const setImageryByName = async (viewer, name, colorCode = null) => {
  saveToCache("mapStyle", name);
  switch (name) {
    case "Earth at Night":
      loadEarthAtNightFromIon(viewer);
      break;
    case "Mapbox Streets":
      await loadMapboxImagery(viewer, "streets-v11");
      break;
    case "Mapbox Outdoors":
      await loadMapboxImagery(viewer, "outdoors-v11");
      break;
    case "Mapbox Light":
      await loadMapboxImagery(viewer, "light-v10");
      break;
    case "Mapbox Dark":
      await loadMapboxImagery(viewer, "dark-v10");
      break;
    case "Bing Maps Aerial":
      loadBingMapsAerialFromIon(viewer);
      break;
    case "GeoTiff Tile":
      loadGeoTiffTileFromIon(viewer, colorCode);
      break;
    case "Bing Maps Aerial with Labels":
      loadBingMapsAerialwithLabelsFromIon(viewer);
      break;
    case "Bing Maps Roads":
      loadBingMapRoadFromIon(viewer);
      break;
    case "Natural Earth II":
      loadNaturalEarthFromIon(viewer);
      break;
    case "ArcGIS World Imagery":
      addArcGISWorldImagery(viewer);
      break;
    case "OpenStreetMap":
      loadOpenStreetMap(viewer);
      break;
    case "Gray":
      loadGray(viewer);
      break;
    case "White":
      loadWhite(viewer);
      break;
    case "Color":
      loadColor(viewer, colorCode);
      saveToCache("mapStyle", name + ":" + colorCode);
      break;
    default:
      console.warn("Unknown imagery name:", name);
      return;
  }
};
